import {
  Box,
  Button,
  Chip,
  ChipProps,
  ClickAwayListener,
  Icon,
  Menu,
  MenuItem,
  Popper,
  Select,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  visuallyHidden,
} from "@outschool/backpack";
import { EnrollmentStatus } from "@outschool/gql-frontend-generated";
import {
  faBadgeCheck,
  faCheckCircle,
  faClock,
  faDiamondExclamation,
  faEllipsisV,
  faPauseCircle,
  faSparkles,
} from "@outschool/icons";
import { Trans, useLocale, useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { adultThreadPathV2 } from "@outschool/routes";
import {
  dayjs,
  formatDate,
  fullDurationStringWithoutTimes,
  startDayShortString,
  timeZoneName,
} from "@outschool/time";
import {
  AvatarImage,
  DEFAULT_AVATAR,
  ErrorMessage,
  ExternalLink,
  TrackedButton,
} from "@outschool/ui-components-shared";
import { Card, Loading, Modal } from "@outschool/ui-legacy-component-library";
import {
  Screen,
  pxToRem,
  useIsMobile,
  useLinkComponent,
  useNavigation,
} from "@outschool/ui-utils";
import _ from "lodash";
import React, { useState } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";

import * as Env from "../../../shared/Env";
import { EnrollmentListByStatusQuery } from "../../../shared/generated/graphql";
import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import { UserMode } from "../../lib/UserMode";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import {
  getEnrollmentListQueryVariables,
  useEnrollmentListQuery,
} from "../../queries/EnrollmentListQuery";
import {
  useGenerateProgressReportMutation,
  useSendProgressReportMutation,
} from "../../queries/SectionMutations";
import { NoteResponseLoading } from "../../routes/SectionAiAssistantPage";
import BoxLink from "../BoxLink";
import Link from "../Link";
import NoteMakerInput from "../NoteMakerInput";
import PageModal from "../PageModal";
import { LearnerBlockedBadge } from "../teacher/LearnerBlockedBadge";
import SectionLearnerModal from "../teacher/SectionLearnerModal";
import TeacherNoteGenerater from "../TeacherNoteGenerater";
import { EnrollOwnLearner } from "./EnrollOwnLearner";

interface EnrollmentList {
  sectionUid: string;
  containerStyle?: SxProps;
  showEnrollOwnLearner?: boolean;
}

type EnrollmentsOrder = {
  property: string | ((e: any) => any);
  order: "asc" | "desc";
}[];

/**
 * Use to distinguish between the version of the list
 * on the classroom tab vs the one on the learners tab
 */
function useIsLearnersTab(section?: SectionType | null) {
  const pathname = Routes.sectionLearnersPath(section ?? "");
  return !!useRouteMatch(pathname);
}

export const EnrollmentList: React.FC<EnrollmentList> = ({
  sectionUid,
  containerStyle,
  showEnrollOwnLearner = false,
}) => {
  const { loading, enrollments, section, waitlistSeats, error, refetch } =
    useEnrollmentListQuery({
      ...getEnrollmentListQueryVariables(sectionUid),
    });

  const isLearnersTab = useIsLearnersTab(section);
  const showWaitlisted =
    section?.activity?.allowWaitlist && (waitlistSeats?.length || 0) > 0;

  if (!section) {
    if (loading) {
      return <Loading />;
    } else {
      return (
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      );
    }
  }

  /*
    withdrawn, ended and enrolled are mutually exclusive, so the ordering becomes;
    (enrolled, sorted alphabetically and by date) ->
    (ended, sorted alphabetically and by date) ->
    (withdrawn, sorted alphabetically and by date).
    Thereafter, duplicates are removed, given that ordering.
    More context: https://outschool.atlassian.net/browse/EP-391
   */
  const enrollmentsOrder: EnrollmentsOrder = [
    { property: "isWithdrawn", order: "asc" },
    { property: "isEnded", order: "asc" },
    { property: "isPaused", order: "asc" },
    { property: "isStartingSoon", order: "asc" },
    { property: "isEnrolled", order: "asc" },
    {
      property: e =>
        e.__typename === "Enrollment"
          ? e.learner.name?.toLowerCase()
          : undefined,
      order: "asc",
    },
    { property: "createdAt", order: "desc" },
  ];

  const uniqEnrollments = _.uniqBy(
    _.orderBy(
      enrollments,
      _.map(enrollmentsOrder, "property"),
      _.map(enrollmentsOrder, "order"),
    ),
    e => {
      if (e.__typename === "Enrollment") {
        return e.learner.uid;
      } else if (e.__typename === "GiftEnrollment") {
        return e.uid;
      } else {
        return undefined;
      }
    },
  );

  const enrolledEnrollments = uniqEnrollments.filter(
    e => e.isEnrolled && !e.isPaused && !e.isStartingSoon,
  );
  const startingSoonEnrollments = uniqEnrollments.filter(e => e.isStartingSoon);
  const pausedEnrollments = uniqEnrollments.filter(e => e.isPaused);
  const pastEnrollments = uniqEnrollments.filter(e => !e.isPaused && e.isEnded);
  const withdrawnEnrollments = uniqEnrollments.filter(e => e.isWithdrawn);

  return (
    <Box
      sx={[
        {
          backgroundColor: "common.white",
          height: "fit-content",
        },
        ...(Array.isArray(containerStyle) ? containerStyle : [containerStyle]),
      ]}
    >
      <NamedEnrollmentList
        enrollmentStatus={EnrollmentStatus.Enrolled}
        enrollments={enrolledEnrollments}
        loading={loading}
        section={section}
        showEmptyMessage={true}
      />

      {startingSoonEnrollments.length > 0 && (
        <NamedEnrollmentList
          enrollmentStatus={EnrollmentStatus.StartingSoon}
          enrollments={startingSoonEnrollments}
          loading={loading}
          section={section}
          showEmptyMessage={true}
        />
      )}
      {pausedEnrollments.length > 0 && (
        <NamedEnrollmentList
          enrollmentStatus={EnrollmentStatus.Paused}
          enrollments={pausedEnrollments}
          loading={loading}
          section={section}
          showEmptyMessage={true}
        />
      )}
      {showWaitlisted && !isLearnersTab && (
        <NamedEnrollmentList
          enrollmentStatus={EnrollmentStatus.Waitlisted}
          waitlistSeats={waitlistSeats}
          loading={loading}
          section={section}
          showEmptyMessage={true}
        />
      )}
      {pastEnrollments.length > 0 && (
        <NamedEnrollmentList
          enrollmentStatus={EnrollmentStatus.Ended}
          enrollments={pastEnrollments}
          loading={loading}
          section={section}
          showEmptyMessage={true}
        />
      )}
      {withdrawnEnrollments.length > 0 && (
        <NamedEnrollmentList
          enrollmentStatus={EnrollmentStatus.Withdrawn}
          enrollments={withdrawnEnrollments}
          loading={loading}
          section={section}
          showEmptyMessage={true}
        />
      )}
      {showEnrollOwnLearner && (
        <EnrollOwnLearner
          sectionUid={sectionUid}
          activity={section?.activity}
          enrollments={enrollments}
          onEnrollmentSuccess={() => refetch()}
        />
      )}
    </Box>
  );
};

type SectionType = NonNullable<EnrollmentListByStatusQuery["section"]>;
type WaitlistSeatsType = NonNullable<SectionType>["waitlistSeats"];
type WaitlistSeatType = NonNullable<WaitlistSeatsType>[number];
type EnrollmentsType = NonNullable<SectionType>[
  | "enrollments"
  | "unclaimedGiftEnrollments"];
type EnrollmentType = NonNullable<SectionType>["enrollments"][number];
type UnclaimedGiftEnrollmentType =
  NonNullable<SectionType>["unclaimedGiftEnrollments"][number];

type NamedEnrollmentListProps = {
  section: SectionType;
  enrollmentStatus: EnrollmentStatus;
  itemStyle?: SxProps;
  activeItemStyle?: SxProps;
  loading: boolean;
  showEmptyMessage?: boolean;
  // props can include EITHER enrollments OR waitlistSeats, not both or neither.
} & (
  | { enrollments: EnrollmentsType; waitlistSeats?: never }
  | { enrollments?: never; waitlistSeats: WaitlistSeatsType }
);

export function NamedEnrollmentList({
  section,
  enrollmentStatus,
  enrollments,
  waitlistSeats,
  loading = false,
  showEmptyMessage,
  itemStyle,
  activeItemStyle,
}: NamedEnrollmentListProps) {
  // @ts-ignore
  const sectionName = {
    [EnrollmentStatus.Enrolled]: "enrolled",
    [EnrollmentStatus.Ended]: "past",
    [EnrollmentStatus.Withdrawn]: "withdrawn",
    [EnrollmentStatus.Paused]: "paused",
    [EnrollmentStatus.Waitlisted]: "waitlisted",
    [EnrollmentStatus.StartingSoon]: "starting soon",
  }[enrollmentStatus];

  const { activity } = section;
  const sectionSize = section?.size_max || 0;
  const showAiTa =
    enrollmentStatus === EnrollmentStatus.Enrolled &&
    !!enrollments?.length &&
    // only for group classes
    (sectionSize > 1 ||
      // OR old style group where max size can be 1
      (activity.hasTeacherSchedule === false &&
        activity.is_ongoing_weekly === true));

  return (
    <Card>
      <EnrollmentListSection
        // @ts-ignore
        sectionName={sectionName}
        section={section}
        enrollments={enrollments}
        waitlistSeats={waitlistSeats}
        loading={loading}
        showEmptyMessage={showEmptyMessage}
        itemStyle={itemStyle}
        activeItemStyle={activeItemStyle}
      />
      {showAiTa && (
        <AiTaSelectLearner
          enrollments={enrollments as NonNullable<SectionType>["enrollments"]}
          sectionUid={section?.uid || ""}
        />
      )}
    </Card>
  );
}

function AiTaSelectLearner({
  enrollments,
  sectionUid,
}: {
  enrollments: NonNullable<SectionType>["enrollments"];
  sectionUid: string;
}) {
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );
  const [learner, setLearner] = React.useState<
    EnrollmentType["learner"] | string
  >(t("Select Learner"));

  if (!sectionUid) {
    return null;
  }

  return (
    <>
      <Box
        sx={(theme: $TSFixMe) => ({
          padding: "1rem",
          backgroundColor: theme.colors.waterslide100,
          display: "flex",
          flexDirection: "column",
          borderRadius: 10,
          gap: "0.5rem",
        })}
      >
        <Typography
          empahsized
          sx={(theme: $TSFixMe) => ({
            color: theme.colors.waterslide800,
            fontWeight: 500,
            fontSize: "1.8rem",
          })}
        >
          {t("Message a specific learner")}
        </Typography>
        <Typography variant="body1">
          {t(
            "Send their parents a custom update instead of the auto parent update",
          )}
        </Typography>
        <Select
          defaultValue={t("Select Learner")}
          selectSx={{ borderRadius: 35 }}
          value={typeof learner === "object" ? learner?.uid : learner}
          size="small"
          onChange={({ target: { value } }) => {
            const foundLearner =
              enrollments?.find(
                enrollment => enrollment?.learner?.uid === value,
              )?.learner || t("Select Learner");
            setLearner(foundLearner);
          }}
        >
          <MenuItem
            disabled
            key={`ai-ta-learner-default`}
            value={t("Select Learner")}
          >
            <Typography sx={{ fontWeight: 500 }}>
              {t("Select Learner")}
            </Typography>
          </MenuItem>
          {enrollments?.length &&
            enrollments.map(enrollment =>
              enrollment?.learner ? (
                <MenuItem
                  key={`ai-ta-learner-${enrollment?.learner?.uid}`}
                  value={enrollment?.learner?.uid}
                >
                  <Typography>{enrollment?.learner?.name}</Typography>
                </MenuItem>
              ) : null,
            )}
        </Select>
      </Box>
      <PageModal
        open={typeof learner === "object" ? true : false}
        onClose={() => setLearner(t("Select Learner"))}
        sx={{ maxWidth: 750 }}
      >
        <AiTaModal learner={learner} sectionUid={sectionUid} t={t} />
      </PageModal>
    </>
  );
}

function AiTaModal({ learner, sectionUid, t }: $TSFixMe) {
  const navigate = useNavigation();
  const generateProgressReport = useGenerateProgressReportMutation();
  const [sendProgressReport, { isSendingProgressReport }] =
    useSendProgressReportMutation();
  const [teacherNotes, setTeacherNotes] = React.useState<string | undefined>();
  const [isCreatingProgressReports, setIsCreatingProgressReports] =
    React.useState<boolean>(false);
  const [generatedLongNote, setGeneratedLongNote] = React.useState<
    string | undefined
  >();
  const [generatedShortNote, setGeneratedShortNote] = React.useState<
    string | undefined
  >();
  const [confimationError, setConfirmationError] = React.useState<
    string | undefined
  >();

  const createProgressReport = async (teacherNotes: string | undefined) => {
    setIsCreatingProgressReports(true);
    if (!teacherNotes || teacherNotes.trim().length === 0) {
      setConfirmationError(t("Please enter notes before clicking button."));
      setIsCreatingProgressReports(false);
      return;
    }
    try {
      const [longResp, shortResp] = await Promise.all([
        generateProgressReport({
          sectionUid: sectionUid,
          notes: teacherNotes,
          reportLength: "FewParagraphs",
        }),
        generateProgressReport({
          sectionUid: sectionUid,
          notes: teacherNotes,
          reportLength: "OneParagraph",
        }),
      ]);
      setGeneratedLongNote(longResp.data.generateProgressReport);
      setGeneratedShortNote(shortResp.data.generateProgressReport);
    } catch (err) {
      console.log(err);
      setConfirmationError(
        t(
          "Sorry, that didn’t work. We’ll look into it. You can reload the page or contact support@outschool.com.",
        ),
      );
      OsPlatform.captureError(err, {
        tags: { component: Component.TeacherTools },
      });
    }
    setIsCreatingProgressReports(false);
  };

  const sendProgressReportMessage = async (
    providedMessage: string | undefined,
    reportLength: string,
    generatedReport: boolean,
    changedGeneratedReport: boolean,
  ) => {
    if (providedMessage === undefined || providedMessage.trim().length === 0) {
      setConfirmationError(
        t("Please provide notes before sending to parents."),
      );
      return;
    }
    const reportDetails = {
      originalNotes: teacherNotes ?? "no teacher note provided",
      reportLength: reportLength,
      didGenerateReport: generatedReport,
      didChangeGeneratedReport: changedGeneratedReport,
    };

    try {
      const messageSent = await sendProgressReport({
        sectionUid: sectionUid,
        text: providedMessage,
        details: reportDetails,
        learnerUid: learner.uid,
      });
      const messageRoute = adultThreadPathV2(
        UserMode.Educator,
        messageSent.data.sendProgressReport[0].thread_uid,
      );
      navigate(messageRoute);
    } catch (err) {
      setConfirmationError(
        t(
          "Sorry, that didn’t work. We’ll look into it. You can reload the page or contact support@outschool.com.",
        ),
      );
      OsPlatform.captureError(err, {
        tags: { component: Component.TeacherTools },
      });
    }
  };
  return (
    <>
      <NoteMakerInput
        learner={learner}
        createProgressReport={createProgressReport}
        teacherNotes={teacherNotes}
        setTeacherNotes={setTeacherNotes}
        confimationError={confimationError}
        setConfirmationError={setConfirmationError}
        isGeneratingProgressReport={isCreatingProgressReports}
        sendProgressReportMessage={sendProgressReportMessage}
        isSendingProgressReport={isSendingProgressReport}
        lastProgressReportDate={undefined}
        isTablet={false}
        textAreaHeight={128}
        Header={({ userTimeZone, lastProgressReportDate }) => (
          <>
            <Box
              flex
              sx={{
                flexDirection: "row",
                justifyContent: "space-between",
                width: "100%",
                mb: "1rem",
              }}
            >
              <Typography variant="h5">
                {t("Tell us how {{name}} did", {
                  name: learner?.name ?? t("they"),
                })}
              </Typography>
              {lastProgressReportDate && (
                <Typography
                  variant="body1"
                  sx={{
                    alignSelf: "center",
                  }}
                >
                  {t(
                    "Last note sent: {{lastProgressReportDate}} {{timeZone}}",
                    {
                      lastProgressReportDate: formatDate(
                        dayjs(lastProgressReportDate),
                        userTimeZone ?? undefined,
                        true,
                      ),
                      timeZone: timeZoneName(userTimeZone ?? undefined),
                    },
                  )}
                </Typography>
              )}
            </Box>
            <Box>
              <Box
                flex
                sx={(theme: $TSFixMe) => ({
                  flexDirection: "row",

                  [theme.breakpoints.down("sm")]: {
                    flexDirection: "column",
                  },
                })}
              >
                <Box
                  flex
                  sx={(theme: $TSFixMe) => ({
                    border: "solid 1px #4CAF50",
                    backgroundColor: "#EEF7EE",
                    flexDirection: "row",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    mr: "1rem",
                    paddingBottom: "1rem",

                    [theme.breakpoints.down("sm")]: {
                      width: "100%",
                    },
                  })}
                >
                  <Icon
                    icon={faCheckCircle}
                    sx={{
                      marginY: "1.2em",
                      marginX: "0.8em",
                      color: "#4CAF50",
                    }}
                  />
                  <Box
                    flex
                    sx={{
                      flexDirection: "column",
                      margin: "0.3em",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={(theme: $TSFixMe) => ({
                        alignSelf: "start",
                        marginY: "0.5em",
                        marginRight: "0.8em",
                        fontWeight: 700,
                        color: theme.palette.success[700],
                      })}
                    >
                      {t("Respect Confidentiality")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={(theme: $TSFixMe) => ({
                        alignSelf: "start",
                        marginRight: "0.7em",
                        color: theme.palette.success[700],
                      })}
                    >
                      {t(
                        "Share feedback only with the learner’s parents or guardians.",
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  flex
                  sx={(theme: $TSFixMe) => ({
                    border: "solid 1px #D32F2F",
                    backgroundColor: "#FFF2EE",
                    flexDirection: "row",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    paddingBottom: "1rem",

                    [theme.breakpoints.down("sm")]: {
                      width: "100%",
                    },
                  })}
                >
                  <Icon
                    icon={faDiamondExclamation}
                    sx={{
                      marginY: "1.2em",
                      marginX: "0.8em",
                      color: "#D32F2F",
                    }}
                  />
                  <Box
                    flex
                    sx={{
                      flexDirection: "column",
                      margin: "0.3em",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        alignSelf: "start",
                        marginY: "0.5em",
                        fontWeight: 700,
                        marginRight: "0.8em",
                        color: "#8F1104",
                      }}
                    >
                      {t("No Personal Information")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        alignSelf: "start",
                        marginRight: "0.7em",
                        color: "#8F1104",
                      }}
                    >
                      {t(
                        "Avoid any personal details about learners (e.g., names, addresses, location, race, gender identity, religion, or health/medical details).",
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography>
                {t(
                  "Create a quick note summarizing the learner’s participation and engagement in class, and what they will learn in their next lesson.",
                )}
              </Typography>
            </Box>
          </>
        )}
        boxSx={{ boxShadow: undefined, padding: 0 }}
      />
      {isCreatingProgressReports && <NoteResponseLoading />}
      {!isCreatingProgressReports && generatedLongNote && (
        <Box
          flex
          sx={{
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <Box
            sx={(theme: $TSFixMe) => ({
              mt: "2rem",
              mb: "1rem",
              borderBottom: `1px solid ${theme.palette.neutral[100]}`,
              gap: "1rem",
            })}
          />
          <TeacherNoteGenerater
            sectionUid={sectionUid}
            teacherNotes={teacherNotes}
            generatedLongNote={generatedLongNote}
            generatedShortNote={generatedShortNote}
            sendProgressReportMessage={sendProgressReportMessage}
            isSendingProgressReport={isSendingProgressReport}
            isGeneratingProgressReport={isCreatingProgressReports}
            isTablet={false}
            textAreaHeight={128}
          />
        </Box>
      )}
    </>
  );
}

/* LIST SECTION */

type EnrollmentListSectionProps = {
  sectionName: string;
} & Omit<NamedEnrollmentListProps, "sections" | "enrollmentStatus">;

export const EnrollmentListSection = ({
  sectionName,
  section,
  enrollments,
  waitlistSeats,
  loading = false,
  showEmptyMessage = false,
  itemStyle,
  activeItemStyle,
}: EnrollmentListSectionProps) => {
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );

  const sectionTitle = _.capitalize(sectionName);
  const numItems = (enrollments ?? waitlistSeats!).length;

  if (!showEmptyMessage && !numItems) {
    return null;
  }

  return (
    <React.Fragment>
      <EnrollmentListSectionHeader>
        {sectionTitle}{" "}
        <Typography variant="inherit" sx={{ color: "grey.500" }}>
          {sectionName === "paused" ? (
            <EnrollmentListPauseDetails section={section} />
          ) : (
            `(${numItems})`
          )}
        </Typography>
      </EnrollmentListSectionHeader>
      {enrollments && enrollments.length ? (
        enrollments.map((e, i) => (
          <EnrollmentListItem
            key={e.uid}
            enrollment={e}
            section={section}
            style={itemStyle}
            activeStyle={activeItemStyle}
            lastItem={i === enrollments.length - 1}
          />
        ))
      ) : waitlistSeats && waitlistSeats.length ? (
        waitlistSeats.map((s, i) => (
          <WaitlistSeatListItem
            key={s.uid}
            waitlistSeat={s}
            section={section}
            style={itemStyle}
            activeStyle={activeItemStyle}
            lastItem={i === waitlistSeats.length - 1}
          />
        ))
      ) : loading ? (
        <Box
          sx={{
            paddingX: "2em",
            paddingY: "1em",
            color: "grey.300",
            ...itemStyle,
          }}
        >
          {t("Loading...")}
        </Box>
      ) : (
        <Box
          sx={{
            paddingX: "2em",
            paddingY: "1em",
            color: "grey.300",
            ...itemStyle,
          }}
        >
          {t("No {{sectionName}} learners", { sectionName })}
        </Box>
      )}
    </React.Fragment>
  );
};

export const EnrollmentListPauseDetails = ({
  section,
}: Pick<NamedEnrollmentListProps, "section">) => {
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );
  if (!section?.enrollments) {
    return null;
  }

  const enrollmentsWithPauses = section.enrollments.filter(
    el => !!el.pauseWindow,
  );
  const activelyPausedEnrollments = enrollmentsWithPauses.filter(
    el => el.isPaused,
  );

  const activelyPaused = activelyPausedEnrollments.length;
  const scheduledPause = enrollmentsWithPauses.length - activelyPaused;

  return !enrollmentsWithPauses.length ? (
    <>(0)</>
  ) : (
    <Typography
      sx={{
        variant: "caption",
        fontWeight: "500",
        fontSize: "0.8em",
        whiteSpace: "nowrap",
      }}
    >
      {t("({{activelyPaused}} Active, {{scheduledPause}} Scheduled)", {
        activelyPaused,
        scheduledPause,
      })}
    </Typography>
  );
};

export const EnrollmentListSectionHeader = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <Box>
    <Typography
      variant="h6"
      sx={{
        pb: "1em",
        pt: "0.25rem",
        lineHeight: "0.8em",
      }}
    >
      {children}
    </Typography>
    <Box
      sx={{
        height: "1px",
        marginBottom: -1,
        backgroundColor: "grey.100",
      }}
    />
  </Box>
);

/* LIST ITEMS */

function ListItemWrapper({
  children,
  style,
  lastItem = false,
}: React.PropsWithChildren<{ style?: SxProps; lastItem: boolean }>) {
  return (
    <Box
      flex
      sx={{
        ...style,
        flexDirection: "column",
        "&:after": !lastItem && {
          content: '""',
          width: "calc(100% - 32px)",
          height: "1px",
          marginBottom: -1,
          backgroundColor: "grey.100",
          alignSelf: "center",
        },
      }}
    >
      {children}
    </Box>
  );
}

interface ListItemPropsBase {
  section: SectionType;
  style?: SxProps;
  activeStyle?: SxProps;
}
export function WaitlistSeatListItem({
  section,
  waitlistSeat,
  style,
  lastItem = false,
}: ListItemPropsBase & {
  waitlistSeat: WaitlistSeatType;
  lastItem: boolean;
}) {
  return (
    <ListItemWrapper style={style} lastItem={lastItem}>
      <WaitlistLearnerDetail section={section} waitlistSeat={waitlistSeat} />
    </ListItemWrapper>
  );
}
export function EnrollmentListItem({
  section,
  enrollment,
  style,
  activeStyle,
  lastItem = false,
}: ListItemPropsBase & {
  enrollment: EnrollmentType | UnclaimedGiftEnrollmentType;
  lastItem: boolean;
}) {
  const learnerOrEnrollment =
    enrollment?.__typename === "Enrollment" ? enrollment?.learner : enrollment;
  const pathname = Routes.sectionLearnerPath(section, learnerOrEnrollment);

  const location = useLocation();
  const isActiveItem = useRouteMatch(pathname);
  const [linkRef, setLinkRef] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    if (isActiveItem && linkRef) {
      linkRef.scrollIntoView({
        block: "center",
      });
    }
  }, [isActiveItem, linkRef]);

  return (
    <Link
      component={BoxLink}
      to={{
        pathname,
        query: location.query,
        state: { noScroll: true },
      }}
      style={{
        display: "block",
      }}
      activeStyle={{
        backgroundColor: "white",
        marginBottom: -1,
        borderTop: "1px solid #DDDFE3",
        borderBottom: "1px solid #DDDFE3",
        boxShadow: "0px 2px 10px rgba(23,25,28,0.1)",
        ...activeStyle,
      }}
      ref={setLinkRef}
    >
      <ListItemWrapper style={style} lastItem={lastItem}>
        {enrollment.__typename === "Enrollment" && (
          <LearnerDetail section={section} enrollment={enrollment} />
        )}
        {enrollment.__typename === "GiftEnrollment" && (
          <UnclaimedGiftDetail enrollment={enrollment} />
        )}
      </ListItemWrapper>
    </Link>
  );
}

function UnclaimedGiftDetail({
  enrollment,
}: {
  enrollment: UnclaimedGiftEnrollmentType;
}) {
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );
  return (
    <Box
      flex
      sx={{
        padding: "1em",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          marginRight: "0.5em",
        }}
      >
        <AvatarImage
          avatar={DEFAULT_AVATAR}
          width={48}
          height={48}
          name="Learner TBD"
        />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          color: "grey.900",
        }}
      >
        <Box
          flex
          sx={(theme: Theme) => ({
            alignItems: "center",
            justifyContent: "space-between",
            [theme.breakpoints.down("sm")]: {
              justifyContent: "flex-start",
            },
          })}
        >
          <Typography variant="h3">{t("Learner TBD")}</Typography>
          <Box
            sx={{
              marginLeft: "0.5em",
              backgroundColor: "#f8f8f9",
              color: "grey.500",
              padding: "0.5em",
              borderRadius: 8,
              whiteSpace: "nowrap",
            }}
          >
            {t("Age TBD")}
          </Box>
        </Box>
        <Box
          flex
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <Box
            sx={{
              fontSize: 14,
            }}
          >
            <Box>{t("Unclaimed Gift")}</Box>
            <Box>
              {t("{{buyerFirstName}} bought this gift", {
                buyerFirstName: User.firstName(enrollment.buyer),
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function LearnerDetail({
  section,
  enrollment,
}: {
  section: SectionType;
  enrollment: EnrollmentType;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isManageEnrollmentModalOpen, setIsManageEnrollmentModalOpen] =
    useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );
  // Only show parent's name and class history if we're in the learners tab
  const isLearnersTab = useIsLearnersTab(section);

  const learner = enrollment.learner!;
  const chipDataEnrolled = useLearnerChip(enrollment);
  const learnerSections = enrollment.otherSections;
  const classHistoryText = enrollment.isFirstOutschoolClass
    ? t("First class on Outschool")
    : enrollment.isRepeatLearnerOfTeacher
    ? t("{{classesWithYou}} classes with you", {
        classesWithYou: learnerSections.length + 1,
      })
    : t("First class with you");
  let pausePeriodText = ``;
  if (enrollment.pauseWindow) {
    pausePeriodText = fullDurationStringWithoutTimes(
      enrollment.pauseWindow.startDate,
      enrollment.pauseWindow.endDate,
    );
  }
  const enrollmentStartDate = enrollment.isStartingSoon
    ? enrollment.ongoingStartTime
    : null;
  const { timeZone } = useTimeZone();
  return (
    <>
      <Box
        flex
        sx={{
          alignItems: "center",
          my: "1rem",
          ...(enrollment.pauseWindow || isLearnersTab ? { mb: 0 } : {}),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AvatarImage
          avatar={learner.avatar || DEFAULT_AVATAR}
          width={36}
          height={36}
          name={learner.name}
          sx={{
            marginRight: "0.5em",
          }}
        />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column", // Changing to column layout
            justifyContent: "space-between",
            color: "grey.900",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                maxWidth: isLearnersTab ? "130px" : "90px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "500",
                whiteSpace: "nowrap",
              }}
            >
              {learner.name}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  fontSize: "0.875em",
                  marginLeft: "0.5em",
                  backgroundColor: "#f8f8f9",
                  color: "grey.500",
                  padding: "0.5em",
                  borderRadius: 8,
                  whiteSpace: "nowrap",
                }}
              >
                {t("Age {{learnerAge}}", { learnerAge: learner.age })}
              </Box>
              {learner.isVerified && isMobile && <VerifiedLearnerBadge />}
              {!isLearnersTab && (
                <LearnerMenu
                  learner={learner}
                  section={section}
                  isHovered={isHovered}
                  onManageEnrollment={() =>
                    setIsManageEnrollmentModalOpen(true)
                  }
                />
              )}
            </Box>
          </Box>
          {chipDataEnrolled && !isLearnersTab && (
            <Box
              sx={{
                marginTop: "0.5em",
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <Tooltip title={chipDataEnrolled.title}>
                <Chip
                  size="small"
                  color={chipDataEnrolled.color}
                  label={chipDataEnrolled.label}
                  icon={chipDataEnrolled.icon}
                />
              </Tooltip>
            </Box>
          )}
          {isLearnersTab && (
            <>
              <Box data-test-id="enrollment-status">{classHistoryText}</Box>
              <Box>
                {t("{{parentFirstName}} enrolled this learner", {
                  parentFirstName: User.firstName(enrollment.parent),
                })}
              </Box>
            </>
          )}
        </Box>

        {learner.isBlocked && <LearnerBlockedBadge />}
      </Box>
      {(enrollment.pauseWindow || isLearnersTab) && (
        <Box
          flex
          sx={{
            justifyContent: "center",
            alignItems: "center",
            ...(enrollment.pauseWindow || isLearnersTab ? { mb: "1rem" } : {}),
          }}
        >
          <Box
            sx={{
              fontSize: 14,
            }}
          >
            {enrollment.pauseWindow &&
              (enrollment.pauseWindow.pauseReason === "payment_failure" ? (
                <Chip
                  label={t("Enrollment Issue")}
                  color="warning"
                  size="small"
                />
              ) : (
                <Box>
                  <Icon
                    icon={faPauseCircle}
                    sx={{
                      color: "warning.main",
                      marginRight: "4px",
                      fontSize: "14px",
                      width: "14px",
                    }}
                  />
                  {pausePeriodText}
                </Box>
              ))}
          </Box>
          {learner.isVerified && !isMobile && <VerifiedLearnerBadge />}
        </Box>
      )}
      {enrollmentStartDate && (
        <Box
          flex
          sx={{
            justifyContent: "center",
            alignItems: "center",
            ...(enrollment.pauseWindow || isLearnersTab ? { mb: "1rem" } : {}),
          }}
        >
          <Box
            sx={{
              fontSize: 14,
            }}
          >
            <Box>
              <Icon
                icon={faClock}
                sx={{
                  color: "info.main",
                  marginRight: "4px",
                  fontSize: "14px",
                  width: "14px",
                }}
              />
              {t("Starts on {{enrollmentStartDate}}", {
                enrollmentStartDate: startDayShortString(
                  enrollmentStartDate,
                  timeZone,
                ),
              })}
            </Box>
          </Box>
          {learner.isVerified && !isMobile && <VerifiedLearnerBadge />}
        </Box>
      )}
      <SectionLearnerModal
        learnerOrEnrollmentUid={learner!.uid}
        sectionUid={section!.uid}
        open={isManageEnrollmentModalOpen}
        onClose={() => setIsManageEnrollmentModalOpen(false)}
      />
    </>
  );
}
function useLearnerChip(seat: EnrollmentType): ChipProps | null {
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );
  const { isRepeatLearnerOfTeacher } = seat;
  const newOnOutSchool = seat.learner?.newOnOutschool;
  if (isRepeatLearnerOfTeacher) {
    return null;
  } else if (newOnOutSchool) {
    return {
      label: t("New on Outschool"),
      color: "primary",
      title: t("This learner has never taken a class on Outschool before."),
    };
  } else {
    return {
      label: t("First class with you"),
      color: "primary",
      title: t("This is the first class this learner is taking with you."),
      icon: <Icon icon={faSparkles} />,
    };
  }
}

function useWaitlistLearnerChip(seat: WaitlistSeatType): ChipProps | null {
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );
  const {
    formerLearner,
    previouslyEnrolled,
    learner: { newOnOutschool },
  } = seat;
  return previouslyEnrolled
    ? {
        label: t("Previously Enrolled"),
        color: "default",
        title: t("This learner was previously enrolled in this section."),
      }
    : formerLearner
    ? {
        label: t("Former Learner"),
        color: "default",
        title: t(
          "This learner was formerly enrolled in another class of yours.",
        ),
      }
    : newOnOutschool
    ? {
        label: t("New on Outschool"),
        color: "primary",
        title: t("This learner has never taken a class on Outschool before."),
      }
    : null;
}

function WaitlistLearnerDetail({
  section,
  waitlistSeat,
}: {
  section: SectionType;
  waitlistSeat: WaitlistSeatType;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );
  const chipData = useWaitlistLearnerChip(waitlistSeat);

  const learner = waitlistSeat.learner!;

  // Only show parent's name and class history if we're in the learners tab
  const pathname = Routes.sectionLearnersPath(section);
  const isLearnersTab = !!useRouteMatch(pathname);

  return (
    <Box
      flex
      sx={{
        padding: "1em",
        alignItems: "center",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        sx={{
          marginRight: "0.5em",
        }}
      >
        <AvatarImage
          avatar={learner?.avatar || DEFAULT_AVATAR}
          width={48}
          height={48}
          name={learner?.name}
        />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          color: "grey.900",
        }}
      >
        <Box
          flex
          sx={{
            justifyContent: "space-between",
            flexGrow: 1,
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={(theme: Theme) => ({
              maxWidth: isLearnersTab ? "130px" : "90px",
              [theme.breakpoints.down("sm")]: { maxWidth: "130px" },
              overflowWrap: "break-word",
            })}
          >
            {learner?.name}
          </Typography>
          <Box
            flex
            sx={{
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                fontSize: "0.875em",
                marginLeft: "0.5em",
                backgroundColor: "#f8f8f9",
                color: "grey.500",
                padding: "0.5em",
                borderRadius: 8,
                whiteSpace: "nowrap",
              }}
            >
              {t("Age {{learnerAge}}", { learnerAge: learner.age })}
            </Box>
            {learner.isVerified && isMobile && <VerifiedLearnerBadge />}
            {!isLearnersTab && (
              <LearnerMenu
                learner={learner}
                section={section}
                parent={waitlistSeat.parent}
                isHovered={isHovered}
              />
            )}
          </Box>
        </Box>
        {chipData && (
          <Tooltip title={chipData.title}>
            <Chip size="small" color={chipData.color} label={chipData.label} />
          </Tooltip>
        )}
        {isLearnersTab && (
          <Box
            flex
            sx={{
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <Box
              sx={{
                fontSize: 14,
              }}
            >
              {isLearnersTab && (
                <>
                  <Box>
                    {t("{{parentFirstName}} waitlisted this learner", {
                      parentFirstName: User.firstName(waitlistSeat.parent),
                    })}
                  </Box>
                </>
              )}
            </Box>
            {learner.isVerified && !isMobile && <VerifiedLearnerBadge />}
          </Box>
        )}
      </Box>
    </Box>
  );
}

interface LearnerMenuProps {
  learner: (EnrollmentType | WaitlistSeatType)["learner"];
  section: SectionType;
  parent?: WaitlistSeatType["parent"];
  isHovered: boolean;
  onManageEnrollment?: () => void;
}

function LearnerMenu({
  learner,
  section,
  parent,
  onManageEnrollment,
}: LearnerMenuProps) {
  const isMobile = Screen.useIsMobile();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = (e?: any) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );
  const Link = useLinkComponent();

  const messagePath = parent
    ? Routes.userConversationsPath(
        UserMode.Educator,
        parent.uid,
        undefined,
        true,
      )
    : Routes.sectionLearnerPath(section, learner);
  const profilePath = Routes.learnerProfilePath(learner);

  const ellipsisButtonSx: SxProps = {
    width: 24,
    height: 24,
    color: "grey.400",
    padding: "2rem",
    marginLeft: "0.5rem",
    transform: "translateX(0.2rem)",
  };
  const ellipsisIconSx: SxProps = {
    width: 24,
    height: 24,
  };
  const modalLinkSx: SxProps = {
    paddingY: "1em",
    textAlign: "left",
  };
  const menuItemSx: SxProps = {
    padding: 0,
    textAlign: "center",
  };
  const menuItemLinkSx: SxProps = {
    width: "100%",
    paddingX: pxToRem(16),
    paddingY: pxToRem(6),
    "&:hover": {
      color: "inherit !important",
      textDecoration: "none !important",
    },
  };

  const openProfileTrackingName =
    "learner_profile.enrollment_list.open_profile";

  const manageEnrollmentLabel = t("Manage Enrollment");
  const messageLabel = parent ? t("Message Learner’s Parent") : t("Message");
  const profileLabel = t("View Profile");

  if (isMobile) {
    return (
      <>
        <Button
          onClick={handleClick}
          variant="text"
          color="inherit"
          sx={ellipsisButtonSx}
        >
          <Icon icon={faEllipsisV} sx={ellipsisIconSx} />
          <Typography style={visuallyHidden}>{t("More")}</Typography>
        </Button>
        <Modal open={open} onClose={handleClose}>
          <Box
            flex
            sx={{
              flexDirection: "column",
            }}
          >
            <Box
              flex
              sx={{
                alignItems: "center",
                alignSelf: "center",
                paddingBottom: "0.5em",
              }}
            >
              <AvatarImage
                avatar={learner?.avatar || DEFAULT_AVATAR}
                width={48}
                height={48}
                name={learner?.name}
              />
              <Typography variant="h3">{learner?.name}</Typography>
            </Box>
            <Button
              component={Link}
              variant="link"
              to={messagePath}
              sx={modalLinkSx}
            >
              {messageLabel}
            </Button>
            <TrackedButton
              variant="link"
              component={Link}
              to={profilePath}
              trackingName={openProfileTrackingName}
              sx={modalLinkSx}
            >
              {profileLabel}
            </TrackedButton>
            {onManageEnrollment && (
              <Button
                type="button"
                variant="link"
                sx={modalLinkSx}
                onClick={() => {
                  onManageEnrollment();
                  handleClose();
                }}
              >
                {manageEnrollmentLabel}
              </Button>
            )}
          </Box>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Button
        onClick={handleClick}
        variant="text"
        color="inherit"
        sx={ellipsisButtonSx}
      >
        <Icon icon={faEllipsisV} sx={ellipsisIconSx} />
        <Typography style={visuallyHidden}>{t("More")}</Typography>
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem sx={menuItemSx}>
          <Button
            variant="link"
            color="inherit"
            component={Link}
            to={messagePath}
            sx={menuItemLinkSx}
          >
            {messageLabel}
          </Button>
        </MenuItem>
        <MenuItem sx={menuItemSx}>
          <TrackedButton
            variant="link"
            color="inherit"
            component={Link}
            to={profilePath}
            trackingName={openProfileTrackingName}
            sx={menuItemLinkSx}
          >
            {profileLabel}
          </TrackedButton>
        </MenuItem>
        {onManageEnrollment && (
          <MenuItem sx={menuItemSx}>
            <Button
              type="button"
              variant="link"
              color="inherit"
              sx={menuItemLinkSx}
              onClick={(e: $TSFixMe) => {
                e.stopPropagation();
                onManageEnrollment();
                handleClose();
              }}
            >
              {manageEnrollmentLabel}
            </Button>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

function VerifiedLearnerBadge() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const hide = () => setAnchorEl(null);

  const locale = useLocale();
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollmentList",
  );

  return (
    <ClickAwayListener onClickAway={hide}>
      <Box>
        <Box
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(anchorEl ? null : e.currentTarget);
            e.stopPropagation();
          }}
        >
          <Icon
            icon={faBadgeCheck}
            sx={{
              color: "#29a877",
              marginLeft: "0.5em",
              fontSize: 24,
              width: "24px",
            }}
          />
        </Box>
        <Popper
          placement="bottom-end"
          open={!!anchorEl}
          anchorEl={anchorEl}
          sx={{ padding: 0 }}
          useArrow={false}
        >
          <Box
            sx={{
              padding: "1em",
              width: "220px",
              cursor: "default",
            }}
          >
            <Trans t={t}>
              This learner{" "}
              <ExternalLink
                url={
                  "https://support.outschool.com/" +
                  locale +
                  "/articles/4063624"
                }
              >
                has been verified
              </ExternalLink>{" "}
              by Outschool staff, and is exempt from turning on their video.
            </Trans>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
